(() => {
  if (customElements.get("product-reviews")) return;

  class ProductReviewsElement extends HTMLElement {
    constructor() {
      super();
      this.onResize = null;
    }

    getInt(value, fallback) {
      const num = parseInt(String(value ?? ""), 10);
      return Number.isFinite(num) ? num : fallback;
    }

    clampInt(value, min, max) {
      const num = this.getInt(value, min);
      return Math.max(min, Math.min(max, num));
    }

    formatReviewDate(dateString) {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return "";

      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    }

    shouldUseSlick() {
      return window.matchMedia("(max-width: 1024px)").matches;
    }

    destroySlickIfNeeded() {
      const sliderEl = this.querySelector("[data-product-reviews-slider]");
      if (!sliderEl) return;

      const $ = window.jQuery;
      if (!($ && $.fn && typeof $.fn.slick === "function")) return;

      const $slider = $(sliderEl);
      if ($slider.hasClass("slick-initialized")) {
        $slider.slick("unslick");
      }
    }

    fetchJson(url, apiKey) {
      return fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          apikey: apiKey,
        },
      }).then((res) => {
        if (!res.ok) {
          throw new Error(
            `Trustpilot API request failed: ${res.status} ${res.statusText}`,
          );
        }
        return res.json();
      });
    }

    renderSummary(summary) {
      const summaryEl = this.querySelector("[data-product-reviews-summary]");
      if (!summaryEl) return;

      const summaryStarsEl = this.querySelector(
        "[data-product-reviews-summary-stars]",
      );
      const starTemplate = this.querySelector(
        "template[data-product-reviews-star]",
      );

      const starsAverageEl = summaryEl.querySelector(
        "[data-product-reviews-summary-stars-average]",
      );
      const totalReviewsEl = summaryEl.querySelector(
        "[data-product-reviews-summary-total]",
      );

      const starsAverageNum = Number(summary?.starsAverage);
      const totalReviewsNum = Number(summary?.numberOfReviews?.total);

      if (summaryStarsEl) {
        summaryStarsEl.innerHTML = "";
        const roundedStars = Number.isFinite(starsAverageNum)
          ? this.clampInt(Math.round(starsAverageNum), 0, 5)
          : 0;

        for (let i = 0; i < roundedStars; i += 1) {
          if (starTemplate?.content) {
            summaryStarsEl.appendChild(starTemplate.content.cloneNode(true));
          }
        }
      }

      if (starsAverageEl) {
        starsAverageEl.textContent = Number.isFinite(starsAverageNum)
          ? starsAverageNum.toFixed(1)
          : "";
      }

      if (totalReviewsEl) {
        totalReviewsEl.textContent = Number.isFinite(totalReviewsNum)
          ? totalReviewsNum.toLocaleString()
          : "";
      }

      const hasSummaryData =
        Number.isFinite(starsAverageNum) && Number.isFinite(totalReviewsNum);

      summaryEl.hidden = !hasSummaryData;
      if (summaryStarsEl) summaryStarsEl.hidden = !hasSummaryData;
    }

    initOrRefreshSlick() {
      const sliderEl = this.querySelector("[data-product-reviews-slider]");
      if (!sliderEl) return;

      const $ = window.jQuery;
      if (!($ && $.fn && typeof $.fn.slick === "function")) return;

      const $slider = $(sliderEl);

      if (!this.shouldUseSlick()) {
        this.destroySlickIfNeeded();
        return;
      }

      if ($slider.hasClass("slick-initialized")) {
        $slider.slick("setPosition");
        return;
      }

      const $sliderContainer = $(this).find(".trustpilot-slider-wrapper");

      $slider.slick({
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: false,
        dots: true,
        infinite: true,
        autoplay: false,
        adaptiveHeight: false,
        centerMode: false,
        variableWidth: false,
        responsive: [
          {
            breakpoint: 568,
            settings: {
              slidesToShow: 1,
              dots: true,
              arrows: false,
            },
          },
        ],
      });
    }

    renderReviews(reviews) {
      const sliderEl = this.querySelector("[data-product-reviews-slider]");
      if (!sliderEl) return;

      const slideTemplate = this.querySelector(
        "template[data-product-reviews-slide]",
      );
      const starTemplate = this.querySelector(
        "template[data-product-reviews-star]",
      );

      sliderEl.innerHTML = "";

      if (!Array.isArray(reviews) || reviews.length === 0) {
        this.setAttribute("data-product-reviews-status", "empty");
        return;
      }

      reviews.forEach((review) => {
        let slide;

        if (slideTemplate && slideTemplate.content) {
          slide = slideTemplate.content.firstElementChild?.cloneNode(true);
        }

        if (!slide) {
          slide = document.createElement("div");
          slide.className = "slide-item";
        }

        const starsContainer = slide.querySelector(
          "[data-product-reviews-stars]",
        );
        const contentEl = slide.querySelector("[data-product-reviews-content]");
        const nameEl = slide.querySelector("[data-product-reviews-name]");

        const stars = this.clampInt(review?.stars, 0, 5);
        const content = String(review?.content ?? "").trim();
        const displayName = String(review?.consumer?.displayName ?? "").trim();
        const createdAt = this.formatReviewDate(review?.createdAt);

        if (starsContainer) {
          starsContainer.innerHTML = "";
          for (let i = 0; i < stars; i += 1) {
            if (starTemplate?.content) {
              starsContainer.appendChild(starTemplate.content.cloneNode(true));
            }
          }
        }

        if (contentEl) contentEl.textContent = content ? content : "";
        if (nameEl) {
          const nameWithDate = createdAt
            ? `${displayName || "Anonymous"} - ${createdAt}`
            : displayName || "Anonymous";
          nameEl.textContent = nameWithDate;
        }

        sliderEl.appendChild(slide);
      });

      this.initOrRefreshSlick();
    }

    connectedCallback() {
      if (this.__initialized) return;
      this.__initialized = true;

      this.onResize = () => {
        if (this.shouldUseSlick()) {
          this.initOrRefreshSlick();
        } else {
          this.destroySlickIfNeeded();
        }
      };

      window.addEventListener("resize", this.onResize, { passive: true });
      const businessUnitId = (
        this.dataset.trustpilotBusinessUnitId || ""
      ).trim();
      const apiKey = (this.dataset.trustpilotApiKey || "").trim();
      const language = (this.dataset.trustpilotLanguage || "").trim();
      const perPageRaw = (this.dataset.trustpilotPerPage || "").trim();
      const pageRaw = (this.dataset.trustpilotPage || "").trim();

      const perPage = this.getInt(perPageRaw || "6", 6);
      const page = this.getInt(pageRaw || "1", 1);

      const skus = (this.dataset.skus || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (!businessUnitId || !apiKey || !skus.length) return;

      const url = new URL(
        `https://api.trustpilot.com/v1/product-reviews/business-units/${encodeURIComponent(
          businessUnitId,
        )}/reviews`,
      );

      url.searchParams.set("sku", skus.join(","));
      if (language) url.searchParams.set("language", language);
      if (!Number.isNaN(perPage) && perPage > 0) {
        url.searchParams.set("perPage", String(perPage));
      }
      if (!Number.isNaN(page) && page > 0) {
        url.searchParams.set("page", String(page));
      }

      this.setAttribute("data-product-reviews-status", "loading");

      const summaryUrl = new URL(
        `https://api.trustpilot.com/v1/product-reviews/business-units/${encodeURIComponent(
          businessUnitId,
        )}`,
      );
      summaryUrl.searchParams.set("sku", skus.join(","));

      this.fetchJson(url.toString(), apiKey)
        .then((data) => {
          this.productReviewsData = data;
          this.setAttribute("data-product-reviews-status", "loaded");
          if (data?.productReviews?.length) {
            this.renderReviews(data?.productReviews);
          } else {
            this.classList.add("hidden");
          }

          this.dispatchEvent(
            new CustomEvent("product-reviews:loaded", {
              detail: data,
              bubbles: true,
            }),
          );

          return this.fetchJson(summaryUrl.toString(), apiKey).catch(
            () => null,
          );
        })
        .then((summaryData) => {
          if (summaryData) {
            this.productReviewsSummaryData = summaryData;
            this.renderSummary(summaryData);
          }
        })
        .catch(() => {
          this.setAttribute("data-product-reviews-status", "error");
          // Intentionally silent to avoid breaking the page if Trustpilot is unavailable.
        });
    }

    disconnectedCallback() {
      if (this.onResize) {
        window.removeEventListener("resize", this.onResize);
      }
      this.destroySlickIfNeeded();
    }
  }

  customElements.define("product-reviews", ProductReviewsElement);
})();
